/**
 * Squad MCP-server client.
 *
 * Mirrors `squadAgent.ts` (same `squadFetch` auth/refresh + `jsonOrThrow`) and
 * covers the three Squad MCP surfaces the agent-connections UI needs:
 *
 *   - catalog            -> GET  /api/mcp-catalog/                 (presets to connect)
 *   - connect a preset   -> POST /api/mcp-catalog/<slug>/connect/  (provisions a workspace server)
 *   - workspace servers  -> GET  /api/mcp-servers/                 (resolve catalog app + id)
 *   - per-agent links    -> GET/POST/PATCH/DELETE /api/agents/<id>/mcp-servers/
 *
 * A catalog entry's `target_app_type` ("" = Core, else "crm"/"hrm") is the same
 * key Core's scope catalog groups scopes by (`app`), so the UI ties "scopes for
 * app X" to "is app X's MCP server connected".
 */

import { jsonInit, jsonOrThrow, squadFetch } from './squadAgent'

/** A preset MCP server the workspace can connect (system-wide catalog). */
export type SquadMcpCatalogEntry = {
  slug: string
  name: string
  description: string
  url: string
  transport: string
  requires_user_jwt: boolean
  /** "" for Core (workspace-tenanted); "crm"/"hrm" for cross-app entries. */
  target_app_type: string
  icon_url: string
  /** True when this workspace already provisioned a server from this entry. */
  is_connected: boolean
}

/** A workspace-scoped MCP server row (provisioned from the catalog or by hand). */
export type SquadMcpServer = {
  id: string
  name: string
  url: string
  transport: string
  is_enabled: boolean
  description: string
  downstream_app_token: string
  catalog_slug: string | null
  /** Mirrors the catalog entry's `target_app_type` (empty for Core). */
  catalog_target_app_type: string
  created_at: string
  modified_at: string
}

/** A per-agent attachment of a workspace MCP server. */
export type SquadAgentMcpLink = {
  id: number
  /** Workspace `SquadMcpServer.id` (uuid). */
  server: string
  server_name: string
  server_url: string
  is_enabled: boolean
  /**
   * Space-delimited Core scope codes granted for THIS connection only (per MCP,
   * per app instance). Empty = identity-only. Core clamps to (ceiling ∩ this ∩
   * user) on token-exchange.
   */
  allowed_scopes: string
  created_at: string
}

type Paged<T> = { results?: T[] } | T[]

const unwrap = <T>(data: Paged<T>): T[] =>
  Array.isArray(data) ? data : (data.results ?? [])

/* ------------------------------------------------------------------ *
 * Catalog
 * ------------------------------------------------------------------ */

export const listMcpCatalog = async (
  appToken: string
): Promise<SquadMcpCatalogEntry[]> => {
  const response = await squadFetch(
    '/api/mcp-catalog/',
    { method: 'GET' },
    appToken
  )

  return unwrap(await jsonOrThrow<Paged<SquadMcpCatalogEntry>>(response))
}

/**
 * Provision (or refresh) a workspace MCP server from a catalog preset.
 * Cross-app entries (`target_app_type` set) require `app_token` (the chosen
 * Core `Application.token`) and accept an optional display `name`.
 */
export const connectMcpCatalog = async (
  appToken: string,
  slug: string,
  body?: { app_token?: string; name?: string }
): Promise<SquadMcpServer> => {
  const response = await squadFetch(
    `/api/mcp-catalog/${slug}/connect/`,
    jsonInit('POST', body ?? {}),
    appToken
  )

  return jsonOrThrow<SquadMcpServer>(response)
}

/* ------------------------------------------------------------------ *
 * Workspace servers
 * ------------------------------------------------------------------ */

export const listWorkspaceMcpServers = async (
  appToken: string
): Promise<SquadMcpServer[]> => {
  const response = await squadFetch(
    '/api/mcp-servers/',
    { method: 'GET' },
    appToken
  )

  return unwrap(await jsonOrThrow<Paged<SquadMcpServer>>(response))
}

/* ------------------------------------------------------------------ *
 * Per-agent links
 * ------------------------------------------------------------------ */

export const listAgentMcpServers = async (
  appToken: string,
  agentId: string
): Promise<SquadAgentMcpLink[]> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/mcp-servers/`,
    { method: 'GET' },
    appToken
  )

  return unwrap(await jsonOrThrow<Paged<SquadAgentMcpLink>>(response))
}

export const attachAgentMcpServer = async (
  appToken: string,
  agentId: string,
  body: { server: string; is_enabled?: boolean }
): Promise<SquadAgentMcpLink> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/mcp-servers/`,
    jsonInit('POST', body),
    appToken
  )

  return jsonOrThrow<SquadAgentMcpLink>(response)
}

export const setAgentMcpServerEnabled = async (
  appToken: string,
  agentId: string,
  linkId: number,
  isEnabled: boolean
): Promise<SquadAgentMcpLink> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/mcp-servers/${linkId}/`,
    jsonInit('PATCH', { is_enabled: isEnabled }),
    appToken
  )

  return jsonOrThrow<SquadAgentMcpLink>(response)
}

/** Replace the delegated scopes granted for one connection (per MCP instance). */
export const setAgentMcpServerScopes = async (
  appToken: string,
  agentId: string,
  linkId: number,
  allowedScopes: string
): Promise<SquadAgentMcpLink> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/mcp-servers/${linkId}/`,
    jsonInit('PATCH', { allowed_scopes: allowedScopes }),
    appToken
  )

  return jsonOrThrow<SquadAgentMcpLink>(response)
}

export const detachAgentMcpServer = async (
  appToken: string,
  agentId: string,
  linkId: number
): Promise<void> => {
  const response = await squadFetch(
    `/api/agents/${agentId}/mcp-servers/${linkId}/`,
    { method: 'DELETE' },
    appToken
  )
  if (!response.ok && response.status !== 404) {
    await jsonOrThrow<unknown>(response) // throws SquadRequestError with body
  }
}
